import { toast, errorToast } from "@/v2-ui/toast"
import { ConfirmButton } from "@/v2-backoffice-client/components/button"
import Badge from "@/v2-backoffice-client/components/badge"
import type { UserFragment } from "@/v2-backoffice-client/user/__types__/user.fragment"
import { useUserContactSyncManyMutation } from "@/v2-backoffice-client/user/contact/sync/many/user.contact.sync.many.mutation"
import { CONTACT_SYNC_STATUS_NOT_LINKED } from "@/v2-backoffice-client/user/contact/userContact.constants"

export type UserContactSyncManyButtonProps = {
  users: UserFragment[]
  onCompleted?(): void
}

// Bulk "Sync to HubSpot" for table selections. Only the selected users
// with no linked contact are actionable — each gets linked to the HubSpot
// contact matching their email, or a contact is created. Users sync
// independently on the server, so one bad row (duplicate email, …) fails
// alone; the outcome toast lists any that did.
function UserContactSyncManyButton(props: UserContactSyncManyButtonProps) {
  const { users, onCompleted } = props
  const [syncMany, { loading, error }] = useUserContactSyncManyMutation()
  errorToast(error, { title: "Failed to sync users to HubSpot" })

  const actionableIds = users
    .filter(
      (u) =>
        (u.ContactSyncStatus as unknown as string) ===
        CONTACT_SYNC_STATUS_NOT_LINKED,
    )
    .map((u) => u.Id)
  const count = actionableIds.length

  return (
    <ConfirmButton
      className="relative"
      isStacked
      iconSrc="hubspot"
      variant="ghost"
      content={`Sync ${count} user(s) not in HubSpot`}
      confirmContent={`Sync ${count} user(s) to HubSpot? Each is linked to the contact matching their email — or a new contact is created.`}
      disabled={!count}
      isLoading={loading}
      onConfirm={() => {
        syncMany({
          variables: { ids: actionableIds },
          onCompleted(data) {
            const results = data.syncManyUserContacts
            const failed = results.filter((r) => r.Error)
            if (failed.length === 0) {
              toast({ title: `Synced ${results.length} user(s) to HubSpot` })
            } else {
              toast({
                variant: "destructive",
                duration: 10000,
                title: `Synced ${results.length - failed.length} of ${results.length} user(s) to HubSpot`,
                description: (
                  <ul>
                    {failed.map((r) => (
                      <li key={r.User.Id}>
                        {r.User.Email}: {r.Error}
                      </li>
                    ))}
                  </ul>
                ),
              })
            }
            onCompleted?.()
          },
        })
      }}
    >
      Sync
      <Badge
        className="absolute top-0 right-0 text-[8px] rounded-full"
        variant="neutral"
        size="tiny"
      >
        {count}
      </Badge>
    </ConfirmButton>
  )
}

export default UserContactSyncManyButton
